import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, X } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { SOCIAL, BRAND } from "../../data/content";

// Google rating prompt. Tapping a star opens the Smokey & Creamy Google review page.
export const RatingPopup = ({ open, onClose }) => {
  const [hover, setHover] = useState(0);
  const [thanks, setThanks] = useState(false);

  const rate = (n) => {
    // Only 4 & 5 star ratings are routed to the public Google review composer.
    // Lower ratings are acknowledged privately and never leave the site.
    if (n >= 4) {
      window.open(SOCIAL.googleReview, "_blank", "noopener,noreferrer");
      onClose();
    } else {
      setThanks(true);
      setTimeout(onClose, 1800);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed bottom-4 left-4 right-4 z-[115] mx-auto w-auto max-w-sm sm:left-auto sm:right-6 sm:bottom-6 sm:mx-0"
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 260 }}
          data-testid="rating-popup"
        >
          <div className="glass relative overflow-hidden rounded-2xl border border-gold/30 p-5 gold-glow">
            <button
              onClick={onClose}
              aria-label="Dismiss"
              data-testid="rating-popup-close"
              className="absolute right-3 top-3 h-8 w-8 grid place-items-center rounded-full text-cream/60 hover:text-cream hover:bg-white/10 transition-colors"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">
              <SiGoogle /> Rate us on Google
            </div>

            {thanks ? (
              <div className="py-2" data-testid="rating-thanks">
                <h3 className="mt-2 font-display text-xl font-semibold text-cream leading-snug">Thanks for your feedback! 🙏</h3>
                <p className="mt-1 text-sm text-cream/60">
                  We truly appreciate you dining with <span className="text-gold-gloss font-display font-semibold">Smokey &amp; Creamy</span>.
                </p>
              </div>
            ) : (
              <>
                <h3 className="mt-2 font-display text-xl font-semibold text-cream leading-snug">
                  Loved every bite? 🔥
                </h3>
                <p className="mt-1 text-sm text-cream/60">
                  Tap a star to rate <span className="text-gold-gloss font-display font-semibold">Smokey &amp; Creamy</span> — it means the world to our team.
                </p>

                <div className="mt-4 flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => rate(n)}
                      onMouseEnter={() => setHover(n)}
                      aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                      data-testid={`rating-star-${n}`}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        size={30}
                        className={`transition-colors ${n <= hover ? "fill-gold text-gold" : "text-cream/25"}`}
                      />
                    </button>
                  ))}
                </div>

                <p className="mt-3 text-[11px] text-cream/40">Opens your Google review for {BRAND.name}.</p>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RatingPopup;
